import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Router } from "@angular/router";

import { getCurrentEntityType, hasPermissionGuard } from "../../core/guards";
import { handleRoutePermission } from "../../core/guards/guards-util";
import { AdaaHelper, isDsV1, isDsV2 } from "../../core/utils";
import { Constants } from "../../shared/constants/constants";
import { PermissionAction } from "../../shared/constants/enums";
import { KpisService } from "../../shared/services";

export const kpiTypeViewAccessGuard = (state: ActivatedRouteSnapshot) => {
  const router = inject(Router);

  const kpiType = state.params["kpiType"];

  if (kpiType === "mokpi" || kpiType === "mtkpi") {
    return isDsV2() ? getKpiPermissionGuard(kpiType, PermissionAction.view) : router.createUrlTree(["/console/kpi"]);
  } else if (kpiType === "nkpi" || kpiType === "uae") {
    return isDsV1() ? getKpiPermissionGuard(kpiType, PermissionAction.view) : router.createUrlTree(["/console/kpi"]);
  }

  return getKpiPermissionGuard(kpiType, PermissionAction.view);
};

export const kpiTypeEditAccessGuard = (state: ActivatedRouteSnapshot) => {
  const router = inject(Router);

  const kpiType = state.params["kpiType"];

  if (kpiType === "mokpi" || kpiType === "mtkpi") {
    return isDsV2() ? getKpiPermissionGuard(kpiType, PermissionAction.manage) : router.createUrlTree(["/console/kpi"]);
  }

  if (kpiType === "opm" || kpiType === "ekpi" || kpiType === "skpi" || kpiType === "srvkpi" || kpiType === "aikpi") {
    const planStatus =
      AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, { type: "prop", property: "status" }) ?? 0;

    return planStatus !== Constants.OBJECT_STATUS.ARCHIVED && planStatus !== Constants.OBJECT_STATUS.CLOSED
      ? getKpiPermissionGuard(kpiType, PermissionAction.manage)
      : router.createUrlTree(["/console/kpi"]);
  }

  return getKpiPermissionGuard(kpiType, PermissionAction.manage);
};

export const kpiTypeCloneAccessGuard = (state: ActivatedRouteSnapshot) => {
  const router = inject(Router);

  const kpiType = state.params["kpiType"];

  if (kpiType === "dtkpi" || kpiType === "ntkpi") {
    if (!AdaaHelper.isPMOEntity()) return router.createUrlTree(["/console/kpi"]);
    return getKpiPermissionGuard(kpiType, PermissionAction.create);
  } else if (kpiType === "skpi") {
    const planStatus =
      AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, { type: "prop", property: "status" }) ?? 0;
    return planStatus !== Constants.OBJECT_STATUS.ARCHIVED && planStatus !== Constants.OBJECT_STATUS.CLOSED
      ? getKpiPermissionGuard(kpiType, PermissionAction.create)
      : router.createUrlTree(["/console/kpi"]);
  }

  return getKpiPermissionGuard(kpiType, PermissionAction.create);
};

export const kpiTypeCreateAccessGuard = (state: ActivatedRouteSnapshot) => {
  const router = inject(Router);

  const kpiType = state.params["kpiType"];

  switch (kpiType) {
    case "dtkpi":
    case "ntkpi": {
      if (!AdaaHelper.isPMOEntity()) return router.createUrlTree(["/console/kpi"]);
      return getKpiPermissionGuard(kpiType, PermissionAction.create);
    }
    case "srvkpi": {
      if (AdaaHelper.isPMOEntity()) return router.createUrlTree(["/console/kpi"]);
      return getKpiPermissionGuard(kpiType, PermissionAction.create);
    }
    case "opm":
    case "skpi":
    case "imkpi": {
      if (AdaaHelper.isPMOEntity()) return router.createUrlTree(["/console/kpi"]);

      const planStatus =
        AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, { type: "prop", property: "status" }) ?? 0;

      return planStatus !== Constants.OBJECT_STATUS.ARCHIVED && planStatus !== Constants.OBJECT_STATUS.CLOSED
        ? getKpiPermissionGuard(kpiType, PermissionAction.create)
        : router.createUrlTree(["/console/kpi"]);
    }
    case "ekpi":
    case "aikpi": {
      const planStatus =
        AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, { type: "prop", property: "status" }) ?? 0;

      return AdaaHelper.isPMOEntity() &&
        planStatus !== Constants.OBJECT_STATUS.ARCHIVED &&
        planStatus !== Constants.OBJECT_STATUS.CLOSED
        ? getKpiPermissionGuard(kpiType, PermissionAction.create)
        : router.createUrlTree(["/console/kpi"]);
    }
    case "iptkpi": {
      return !AdaaHelper.isPMOEntity()
        ? getKpiPermissionGuard(kpiType, PermissionAction.create)
        : router.createUrlTree(["/console/kpi"]);
    }
    case "mokpi": {
      return isDsV2() && !AdaaHelper.isPMOEntity()
        ? getKpiPermissionGuard(kpiType, PermissionAction.create)
        : router.createUrlTree(["/console/kpi"]);
    }
    case "mtkpi": {
      return isDsV2()
        ? getKpiPermissionGuard(kpiType, PermissionAction.create)
        : router.createUrlTree(["/console/kpi"]);
    }
    default:
      return getKpiPermissionGuard(kpiType, PermissionAction.create);
  }
};

const getKpiPermissionGuard = (kpiType: string, permissionAction: PermissionAction) => {
  const kpisService = inject(KpisService);
  const permissionId = kpisService.getKpiPermission(kpisService.getKpiTypeId(kpiType));

  return handleRoutePermission({
    actor: {
      modifier: "and",
      permissions: [
        {
          permissionAction: permissionAction,
          objectTypeId: permissionId,
        },
      ],
    },
  });
};

export const auditIssuesViewAccessGuard = [
  hasPermissionGuard({
    actor: {
      modifier: "and",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.AUDIT_RESULTS,
        },
      ],
    },
    redirectTo: "/403",
  }),
];

export const viewImportExportKpiPagesGuards = [
  hasPermissionGuard({
    actor: {
      modifier: "and",
      permissions: [
        {
          permissionAction: PermissionAction.execute,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.EXPORT_DATA,
        },
      ],
    },
    redirectTo: "/403",
  }),
];

export const kpiPMOGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const kpiType = route.paramMap.get("kpiType");

  if (kpiType === "nkpi" || kpiType === "uae") {
    return AdaaHelper.isPMOEntity() ? true : router.createUrlTree(["/403"]);
  }
  return true;
};

const kpiTypesBlockedByEntityType: Record<number, string[]> = {
  [Constants.ENTITY_TYPES.EXTERNAL]: ["skpi", "ekpi", "opm", "srvkpi"],
  [Constants.ENTITY_TYPES.PARTICIPATING]: ["skpi", "ekpi", "opm", "srvkpi", "aikpi"],
};

export const kpiEntityTypeGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const kpiType = route.paramMap.get("kpiType") ?? "";
  const blockedKpiTypes = kpiTypesBlockedByEntityType[getCurrentEntityType()] ?? [];

  return blockedKpiTypes.includes(kpiType) ? router.createUrlTree(["/403"]) : true;
};
